import { Activity, ClipboardList, Eye, ShieldCheck } from 'lucide-react';
import { cn } from '../ui/utils';
import { PRIORITY_LABEL, PRIORITY_STYLES, type OrganScore } from './organHealth';

const ICONS = {
  check: ShieldCheck,
  eye: Eye,
  clipboard: ClipboardList,
  alert: Activity,
} as const;

/**
 * The COMPOSITE's monitoring priority (rubric v0.12) — how hard to work the
 * problem, which is a different question from how damaged the organ is.
 *
 * Deliberately never looks like a {@link BandChip}. A band chip is FILLED with
 * a tinted severity accent; this one is always OUTLINED. That shape difference
 * is the safety property: risk moves the composite score, so it moves priority
 * — and a clinician who read "acute" as a severity grade would believe the
 * organ had failed when its function may be intact. Urgency at the top two
 * levels is carried by border/text intensity, never by borrowing the severity
 * ramp.
 *
 * Renders nothing when the score carries no priority: older payloads predate
 * v0.12, and an absent priority is unknown, not "routine".
 */
export function PriorityChip({
  organ,
  size = 'sm',
}: {
  organ: OrganScore;
  size?: 'sm' | 'md';
}) {
  if (!organ.priority) return null;

  const style = PRIORITY_STYLES[organ.priority];
  const Icon = ICONS[style.icon];
  const pad = size === 'md' ? 'px-3 py-1 text-sm' : 'px-2 py-0.5 text-xs';
  const iconSize = size === 'md' ? 'size-4' : 'size-3';

  return (
    <span
      className={cn(
        // `bg-transparent` is not decorative — see the note above.
        'inline-flex items-center gap-1.5 rounded-sm border bg-transparent font-medium',
        pad,
        style.outline,
      )}
      // The word alone ("Monitor") does not say which axis it describes, so the
      // accessible name spells it out. Screen-reader users get the same
      // severity/priority separation the outline gives sighted users.
      aria-label={`Monitoring priority: ${PRIORITY_LABEL[organ.priority]}`}
    >
      <Icon className={iconSize} aria-hidden="true" />
      {PRIORITY_LABEL[organ.priority]}
    </span>
  );
}
